import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faComment, faToggleOn, faToggleOff } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../utils/colors.jsx';
import { useChannelSettings } from '../context/ChannelSettingsContext';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { USER_ROLES } from '../utils/constants';
import { useMessageAlert } from '../components/common/MessageAlert';
import { useTranslation } from 'react-i18next';

const ChannelSettingsPage = () => {
  const { t } = useTranslation();
  const CH = t('channelSettings', { returnObjects: true });
  const { emailEnabled, whatsappEnabled, loading, updateChannelSetting } = useChannelSettings();
  const { userRole } = useRoleAccess();
  const { showSuccess, showError } = useMessageAlert();
  const [saving, setSaving] = useState(null);

  const isSuperAdmin = userRole === USER_ROLES.SUPER_ADMIN;

  const handleToggle = async (channel, currentValue) => {
    if (!isSuperAdmin) return;
    setSaving(channel);
    try {
      await updateChannelSetting(channel, !currentValue);
      const channelLabel = channel === 'email' ? CH.channels.email.label : CH.channels.whatsapp.label;
      const state = !currentValue ? CH.status.enabled.toLowerCase() : CH.status.disabled.toLowerCase();
      showSuccess(t('channelSettings.notifications.updated', { channel: channelLabel, state }));
    } catch (err) {
      const channelLabel = channel === 'email' ? CH.channels.email.label : CH.channels.whatsapp.label;
      showError(err?.response?.data?.error || t('channelSettings.notifications.updateFailed', { channel: channelLabel }));
    } finally {
      setSaving(null);
    }
  };

  const channels = [
    {
      key: 'email',
      label: CH.channels.email.label,
      description: CH.channels.email.description,
      icon: faEnvelope,
      enabled: emailEnabled,
      iconColor: UI_COLORS.STATUS.INFO,
    },
    {
      key: 'whatsapp',
      label: CH.channels.whatsapp.label,
      description: CH.channels.whatsapp.description,
      icon: faComment,
      iconColor: '#25D366',
      enabled: whatsappEnabled,
    },
  ];

  return (
    <div className="p-6 max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
          {CH.title}
        </h1>
        <p className="mt-1 text-sm" style={{ color: UI_COLORS.TEXT.MUTED }}>
          {CH.subtitle}
        </p>
        {!isSuperAdmin && (
          <div
            className="mt-3 px-4 py-2 rounded-lg text-sm"
            style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1), color: UI_COLORS.STATUS.WARNING }}
          >
            {CH.superAdminOnlyBanner}
          </div>
        )}
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {channels.map(({ key, label, description, icon, iconColor, enabled }) => (
            <div
              key={key}
              className="flex items-center justify-between p-5 rounded-xl border"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.5),
                borderColor: enabled
                  ? colorHelpers.withOpacity(iconColor, 0.3)
                  : colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2),
              }}
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: colorHelpers.withOpacity(iconColor, 0.12) }}
                >
                  <FontAwesomeIcon icon={icon} style={{ color: iconColor }} />
                </div>
                <div>
                  <div className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{label}</div>
                  <div className="text-xs mt-0.5" style={{ color: UI_COLORS.TEXT.MUTED }}>{description}</div>
                </div>
              </div>

              <button
                onClick={() => handleToggle(key, enabled)}
                disabled={!isSuperAdmin || saving === key}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: enabled
                    ? colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.1)
                    : colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08),
                  color: enabled ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.TEXT.MUTED,
                  border: `1px solid ${enabled ? colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.3) : colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2)}`,
                }}
              >
                <FontAwesomeIcon icon={enabled ? faToggleOn : faToggleOff} className="text-lg" />
                {saving === key ? t('common.actions.saving') : enabled ? CH.status.enabled : CH.status.disabled}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChannelSettingsPage;
